import {
  useAddSalesMutation,
  useLazyGetBankNameQuery,
  useLazyGetContractExpiryDateQuery,
  useLazyGetLeadDetailsQuery,
  useLazyGetMortgageAssessmentQuery,
  useLazyGetPaymentListQuery,
  useLazyGetPaymentMethodQuery,
  useLazyGetPaymentModeQuery,
  useLazyGetProjectActiveBookingPlatformsQuery,
  useLazyGetSaleEventQuery,
  useLazyGetUnitsQuery,
  useLazyGetValidateReferenceNumberQuery,
} from "features/leads/api"
import { useLazyGetProjectPromoTagsQuery } from "features/projects/api"
import {
  useGetProjectsQuery,
  useLazyGetClientIBANQuery,
  useLazyGetCountriesQuery,
  useLazyGetTaskTypeQuery,
  useLazyGetVIBANForContractQuery,
} from "features/shared/api"
import { useLazyGetClientAnswerQuery } from "features/tasks/api"
import { useMemo } from "react"

const paymentModeKeyMap = response =>
  response?.reduce((acc, parent) => {
    acc[parent?.key] = {
      meta: {
        ...parent,
      },
      extraFields: parent?.PaymentModeAttachmentTypeMappings?.reduce((childAcc, child) => {
        childAcc[child?.AttachmentType?.key] = {
          id: child?.id,
          attachmentTypeId: child?.AttachmentType?.id,
          key: child?.AttachmentType?.key,
          name: child?.AttachmentType?.name,
          required: child?.required,
        }
        return childAcc
      }, {}),
    }
    return acc
  }, {})

const useAddSalesApis = () => {
  const [getCountries, countriesQuery] = useLazyGetCountriesQuery()
  const projectsQuery = useGetProjectsQuery({
    skipPpaExpired: true,
    financials: true,
    paymentInfo: true,
    withSaleableListings: true,
    status: "on",
  })

  const [getUnits, unitsQuery] = useLazyGetUnitsQuery()
  const [getClientAnswers, clientAnswersQuery] = useLazyGetClientAnswerQuery()
  const [getPaymentMethod, paymentMethodsQuery] = useLazyGetPaymentMethodQuery()
  const [getActiveBookingPlatforms, activeBookingPlatformsQuery] = useLazyGetProjectActiveBookingPlatformsQuery()
  const [getLeadDetails, leadDetailsQuery] = useLazyGetLeadDetailsQuery()
  const [getMortgageAssessment, mortgageQuery] = useLazyGetMortgageAssessmentQuery()
  const [getPaymentMode, paymentModeQuery] = useLazyGetPaymentModeQuery()
  const [getBankName, banksQuery] = useLazyGetBankNameQuery()
  const [addSales, addSalesMutation] = useAddSalesMutation()
  const [getPaymentList, paymentListQuery] = useLazyGetPaymentListQuery()
  const [getSaleEvent, saleEventQuery] = useLazyGetSaleEventQuery()
  const [getProjectPromoTags, promoTagsQuery] = useLazyGetProjectPromoTagsQuery()
  const [getTaskTypes, taskTypesQuery] = useLazyGetTaskTypeQuery()
  const [validateReference, referenceQuery] = useLazyGetValidateReferenceNumberQuery()
  const [getContractExpiry, contractExpiryQuery] = useLazyGetContractExpiryDateQuery()
  const [getClientIban, clientIbanQuery] = useLazyGetClientIBANQuery()
  const [getViban, vibanQuery] = useLazyGetVIBANForContractQuery()

  // Separate data objects
  const apiData = {
    units: unitsQuery?.data,
    answerData: clientAnswersQuery?.data,
    modeOfPayments: paymentMethodsQuery?.data,
    activeBookingPlatforms: activeBookingPlatformsQuery?.data,
    leadSalesInfo: leadDetailsQuery?.data,
    mortgageAssessment: mortgageQuery?.data,
    paymentMode: paymentModeQuery?.data,
    banks: banksQuery?.data,
    paymentList: paymentListQuery?.data,
    saleEvent: saleEventQuery?.data,
    promoTags: promoTagsQuery?.data,
    countries: countriesQuery?.data?.countries ?? [],
    nationalities: countriesQuery?.data?.nationalities ?? [],
    projects: projectsQuery?.data,
    taskTypes: taskTypesQuery?.data,
    contractExpiry: contractExpiryQuery?.data,
    referenceValidity: referenceQuery?.data,
    clientIban: clientIbanQuery?.data,
    viban: vibanQuery?.data,
    paymentModesMap: paymentModeKeyMap(paymentMethodsQuery?.data),
  }

  const loadingStatus = {
    isFetchingUnits: unitsQuery?.isFetching,
    fetchClientAnswers: clientAnswersQuery?.isFetching,
    isFetchingPaymentMethods: paymentMethodsQuery?.isFetching,
    isFetchingActiveBookingPlatforms: activeBookingPlatformsQuery?.isFetching,
    isFetchingLeadDetails: leadDetailsQuery?.isFetching,
    isFetchingMortgageAssessment: mortgageQuery?.isFetching,
    isFetchingPaymentModes: paymentModeQuery?.isFetching,
    isFetchingBanks: banksQuery?.isFetching,
    fetchPaymentList: paymentListQuery?.isFetching,
    saleEventLoading: saleEventQuery?.isLoading,
    promoTagsLoading: promoTagsQuery?.isLoading,
    isFetchingCountries: countriesQuery?.isFetching,
    isFetchingProjects: projectsQuery?.isFetching,
    isFetchingTaskTypes: taskTypesQuery?.isFetching,
    isError: addSalesMutation?.isError,
    isLoading: addSalesMutation?.isLoading,
    referenceError: referenceQuery?.isError,
    isFetchingContractExpiry: contractExpiryQuery?.isFetching,
    fetchingVIBAN: vibanQuery?.isFetching,
    fetchingIBAN: clientIbanQuery?.isFetching,
  }

  const actions = {
    getUnits,
    getClientAnswers,
    getPaymentMethod,
    getActiveBookingPlatforms,
    getLeadDetails,
    getMortgageAssessment,
    getPaymentMode,
    getBankName,
    addSales,
    getPaymentList,
    getSaleEvent,
    getProjectPromoTags,
    getCountries,
    getTaskTypes,
    reset: addSalesMutation?.reset,
    getContractExpiry,
    validateReference,
    getClientIban,
    getViban,
  }

  // Memoize all together
  return useMemo(
    () => ({
      ...apiData,
      ...loadingStatus,
      error: addSalesMutation?.error,
      ...actions,
    }),
    [...Object.values(apiData), ...Object.values(loadingStatus), addSalesMutation?.error]
  )
}

export default useAddSalesApis
